import Knock, { type PreferenceSet } from '@knocklabs/client';
import {
  NOTIFICATION_CHANNEL_TYPES,
  NOTIFICATION_WORKFLOW_IDENTIFIERS,
  NOTIFICATION_WORKFLOW_METADATA,
} from '@piccolohealth/pbs-common';
import { ChakraV3, Spin } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSwitch } from '../../../components/forms/HookedSwitch';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { useAppContext } from '../../../hooks/useAppContext';

type FormValues = PreferenceSet;

export const NotificationPreferencesForm = () => {
  const { user, config, successToast, errorToast } = useAppContext();

  const knockClient = React.useMemo(() => {
    const knock = new Knock(config.knock.apiKey);
    knock.authenticate(user.id);
    return knock;
  }, [config.knock.apiKey, user.id]);

  const methods = useForm<PreferenceSet>({
    defaultValues: {},
  });
  const { reset } = methods;

  const preferences = methods.watch();

  const fetchPreferences = React.useCallback(async () => {
    const preferences = await knockClient.preferences.get().catch(() => {
      return {} as PreferenceSet;
    });

    // Add any missing workflow/channel type preferences
    const defaultedWorkflowPreferences = NOTIFICATION_WORKFLOW_IDENTIFIERS.reduce<
      Record<string, { channel_types: Record<string, boolean> }>
    >((acc, workflowId) => {
      acc[workflowId] = {
        channel_types: NOTIFICATION_CHANNEL_TYPES.reduce((acc, channelType) => {
          const x =
            P.get(preferences, `workflows.${workflowId}.channel_types.${channelType.id}`) ?? true;

          return Object.assign(acc, {
            [channelType.id]: x,
          });
        }, {}),
      };

      return acc;
    }, {});

    reset({
      ...preferences,
      workflows: defaultedWorkflowPreferences,
    });
  }, [knockClient.preferences, reset]);

  // Read the preferences for the current user
  React.useEffect(() => {
    void fetchPreferences();
  }, [fetchPreferences]);

  const onSubmit = async (values: FormValues) => {
    const updatedReferences = await knockClient.preferences.set({
      ...preferences,
      ...values,
    });

    await knockClient.preferences
      .set(updatedReferences)
      .catch((err) => {
        errorToast(`Error updating user profile: ${err.message}`);
      })
      .then(() => {
        successToast('User profile updated successfully');
        void fetchPreferences();
      });
  };

  // Show a spinner here or something to indicate prefs are loading
  if (P.isEmptyObject(preferences)) {
    return <Spin />;
  }

  const workflows = NOTIFICATION_WORKFLOW_IDENTIFIERS.map((workflow) => ({
    id: workflow,
    ...NOTIFICATION_WORKFLOW_METADATA[workflow],
  }));

  const groupedWorkflows = P.groupBy(workflows, (workflow) => workflow.category);

  const content = P.listify(groupedWorkflows, (groupedWs, category) => {
    const workflows = groupedWs ?? [];
    return (
      <ChakraV3.Stack gap={4} mt={4}>
        <ChakraV3.Text fontSize='md' fontWeight='semibold'>
          {category}
        </ChakraV3.Text>

        <ChakraV3.Stack gap={4}>
          {workflows.map((workflow) => (
            <ChakraV3.HStack key={workflow.name} align='start' gap={32}>
              <ChakraV3.Stack gap={0} w='sm'>
                <ChakraV3.Text fontSize='sm' fontWeight='semibold'>
                  {workflow.name}
                </ChakraV3.Text>
                <ChakraV3.Text fontSize='sm' color='secondary'>
                  {workflow.description}
                </ChakraV3.Text>
              </ChakraV3.Stack>
              <ChakraV3.Stack gap={2} flexShrink={0}>
                {NOTIFICATION_CHANNEL_TYPES.map(({ id, name }) => (
                  <ChakraV3.HStack key={id} align='center'>
                    <HookedSwitch
                      colorPalette='brand'
                      name={`workflows.${workflow.id}.channel_types.${id}`}
                    />
                    <ChakraV3.Text fontSize='xs' fontWeight='semibold'>
                      {name}
                    </ChakraV3.Text>
                  </ChakraV3.HStack>
                ))}
              </ChakraV3.Stack>
            </ChakraV3.HStack>
          ))}
        </ChakraV3.Stack>
      </ChakraV3.Stack>
    );
  });

  return (
    <FormProvider {...methods}>
      <ChakraV3.Stack
        as='form'
        display='contents'
        onSubmit={methods.handleSubmit(onSubmit)}
        gap={6}
      >
        {P.intersperse(content, () => (
          <ChakraV3.Separator />
        ))}
        <ChakraV3.HStack>
          <HookedSubmitButton>Save</HookedSubmitButton>
        </ChakraV3.HStack>
      </ChakraV3.Stack>
    </FormProvider>
  );
};
